import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  ChevronDown,
  Eraser,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const DocumentFilter = ({
  onNewClick,
  onFilterChange,
  onRefresh,
  /** Nhóm đang chọn từ cây thư mục bên trái (trên desktop) */
  lockedGroupId = "",
  groups = [],
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filters, setFilters] = useState({
    keyword: "",
    group_id: lockedGroupId || "",
    status: "",
    from_date: "",
    to_date: "",
  });
  const [debouncedKeyword] = useDebounce(filters.keyword, 400);

  const STATUS_OPTIONS = [
    { value: "active", label: "Hiệu lực" },
    { value: "expired", label: "Hết hiệu lực" },
    { value: "inactive", label: "Ngừng dùng" },
  ];

  // Options cây nhóm tài liệu cho dropdown bộ lọc
  const groupOptions = useMemo(() => {
    if (!Array.isArray(groups) || groups.length === 0) return [];
    const roots = groups.filter((g) => !g.parent_id);
    const options = [];
    roots.forEach((root) => {
      options.push({
        value: String(root.id),
        label: `📁 ${root.group_name || root.name}`,
      });
      const children = groups.filter(
        (c) => String(c.parent_id) === String(root.id)
      );
      children.forEach((child) => {
        options.push({
          value: String(child.id),
          label: `　↳ ${child.group_name || child.name}`,
        });
      });
    });
    // Nhóm mồ côi (nếu có)
    groups.forEach((g) => {
      if (
        g.parent_id &&
        !roots.some((r) => String(r.id) === String(g.parent_id)) &&
        !options.some((o) => o.value === String(g.id))
      ) {
        options.push({
          value: String(g.id),
          label: g.group_name || g.name,
        });
      }
    });
    return options;
  }, [groups]);

  // Đồng bộ group từ cây thư mục trên desktop
  useEffect(() => {
    setFilters((prev) => {
      if (prev.group_id === (lockedGroupId || "")) return prev;
      return { ...prev, group_id: lockedGroupId || "" };
    });
  }, [lockedGroupId]);

  // 📱 Responsive check (< 1024px là mobile/tablet không có sidebar)
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = {
      keyword: "",
      group_id: isMobile ? "" : (lockedGroupId || ""),
      status: "",
      from_date: "",
      to_date: "",
    };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const isFiltering = [
    filters.keyword,
    isMobile ? filters.group_id : "",
    filters.status,
    filters.from_date,
    filters.to_date,
  ].some((v) => v !== "");
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  // ==================== MOBILE ====================
  if (isMobile) {
    const hasAdvancedFilters = Boolean(
      filters.group_id || filters.status || filters.from_date || filters.to_date
    );

    const activeGroupName = filters.group_id
      ? groups.find((g) => String(g.id) === String(filters.group_id))?.group_name ||
        groups.find((g) => String(g.id) === String(filters.group_id))?.name ||
        "Nhóm"
      : null;

    return (
      <div className="mb-4 space-y-2.5">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm kiếm tài liệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-10 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
            {filters.keyword && (
              <button
                type="button"
                onClick={() => updateFilter("keyword", "")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
              >
                <Eraser size={14} />
              </button>
            )}
          </div>
          <ActionButton
            icon={RotateCw}
            onClick={() => onRefresh?.({ ...filters, keyword: debouncedKeyword })}
            title="Làm mới danh sách"
          />
          <div className="relative">
            <ActionButton
              icon={SlidersHorizontal}
              onClick={() => setAdvanced(!advanced)}
              active={advanced || hasAdvancedFilters}
              title="Lọc nâng cao"
            />
            {hasAdvancedFilters && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-600 ring-2 ring-white" />
            )}
          </div>
        </div>

        {isFiltering && (
          <div className="flex items-center justify-between bg-red-50/80 border border-red-200/80 rounded-xl px-3 py-1.5 text-xs text-red-700">
            <span className="font-semibold text-[11px] truncate mr-2">
              {activeGroupName ? (
                <>
                  Đang lọc:{" "}
                  <span className="font-bold underline text-red-800">
                    {activeGroupName}
                  </span>
                  {filters.status || filters.from_date || filters.to_date ? " (+bộ lọc)" : ""}
                </>
              ) : (
                "Đang áp dụng bộ lọc"
              )}
            </span>
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 font-bold text-[11px] text-red-600 hover:text-red-800 uppercase tracking-wider shrink-0"
            >
              <Eraser size={13} />
              Xóa bộ lọc
            </button>
          </div>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc nâng cao"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  clearFilters();
                  setAdvanced(false);
                }}
                className="flex-1 py-3.5 border border-gray-200 text-gray-700 rounded-xl font-bold text-sm"
              >
                XÓA LỌC
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 py-3.5 bg-red-600 text-white rounded-xl font-bold text-sm shadow-md shadow-red-200"
              >
                ÁP DỤNG
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox
              label="Nhóm tài liệu"
              value={filters.group_id}
              options={groupOptions}
              onChange={(v) => updateFilter("group_id", v)}
            />
            <SelectBox
              label="Trạng thái"
              value={filters.status}
              options={STATUS_OPTIONS}
              onChange={(v) => updateFilter("status", v)}
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Từ ngày
                </label>
                <input
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => updateFilter("from_date", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-3 rounded-xl text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all font-medium shadow-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Đến ngày
                </label>
                <input
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => updateFilter("to_date", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-3 rounded-xl text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all font-medium shadow-sm"
                />
              </div>
            </div>
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // ==================== DESKTOP ====================
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm kiếm theo tên tài liệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton
            icon={RotateCw}
            onClick={() => onRefresh?.({ ...filters, keyword: debouncedKeyword })}
            title="Làm mới"
          />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title="Lọc nâng cao"
          />

          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2"
            >
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        {hasPermission("hrm.documents", "C") && (
          <button
            type="button"
            onClick={onNewClick}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
          >
            <Plus size={18} />
            Thêm tài liệu
          </button>
        )}
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Trạng thái"
                value={filters.status}
                options={STATUS_OPTIONS}
                onChange={(v) => updateFilter("status", v)}
              />
              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Từ ngày
                </label>
                <input
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => updateFilter("from_date", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-2xl text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all font-medium shadow-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Đến ngày
                </label>
                <input
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => updateFilter("to_date", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-2xl text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all font-medium shadow-sm"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentFilter;
